import React from 'react';
import { styles } from '../../../utils/styles';
import { CollapsibleSectionHeader } from './components/CollapsibleSectionHeader';
import { DropdownIcon } from './components/DropdownIcon';

type ProductListingProps = {
    id?: string;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    style: 'Grid' | 'List';
    productsPerRow: string;
    productsLoading: 'Infinite Scroll' | 'Load More Button';
    scrollBarAfterRows: string;
    showButtonAfterProducts: string;
    scrollBarAfterProducts: string;
    onConfigChange: (key: string, value: any) => void;
}

export const ProductListing= ({ isExpanded, onToggleExpanded, style, productsPerRow, productsLoading, scrollBarAfterRows, showButtonAfterProducts, scrollBarAfterProducts, onConfigChange }: ProductListingProps) => {
    return (
        <div className="tw-border-b tw-border-[#D1D1D6]">
            <CollapsibleSectionHeader
                title="Product Listing"
                isExpanded={isExpanded}
                onToggle={onToggleExpanded}
            />

            {/* Expanded Product Listing Content */}
            {isExpanded && (
                <div className="tw-px-4 tw-pb-4">
                    {/* Style */}
                    <div className="tw-mb-4">
                        <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                            Style
                        </label>
                        <div className="tw-flex tw-items-center tw-space-x-6">
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="radio"
                                    name="style"
                                    value="Grid"
                                    checked={style === 'Grid'}
                                    onChange={() => onConfigChange('style', 'Grid')}
                                    className="tw-mr-2 tw-accent-[#091CAA]"
                                />
                                <span className="tw-text-sm tw-text-[#172B4D]">Grid</span>
                            </label>
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="radio"
                                    name="style"
                                    value="List"
                                    checked={style === 'List'}
                                    onChange={() => onConfigChange('style', 'List')}
                                    className="tw-mr-2 tw-accent-[#091CAA]"
                                />
                                <span className="tw-text-sm tw-text-[#172B4D]">List</span>
                            </label>
                        </div>
                    </div>
                    
                    {/* Products per Row - Only shown for Grid style */}
                    {style === 'Grid' && (
                        <div className="tw-mb-4">
                            <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                Products per Row
                            </label>
                            <div className={styles.selectContainer}>
                                <select
                                    value={productsPerRow}
                                    onChange={(e) => onConfigChange('productsPerRow', e.target.value)}
                                    className={styles.select}
                                >
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                </select>
                                <div className={styles.selectIcon}>
                                    <DropdownIcon />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Products Loading */}
                    <div className="tw-mb-4">
                        <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                            Products Loading
                        </label>
                        <div className="tw-flex tw-items-center tw-space-x-6">
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="radio"
                                    name="productsLoading"
                                    value="Infinite Scroll"
                                    checked={productsLoading === 'Infinite Scroll'}
                                    onChange={() => onConfigChange('productsLoading', 'Infinite Scroll')}
                                    className="tw-mr-2 tw-accent-[#091CAA]"
                                />
                                <span className="tw-text-sm tw-text-[#172B4D]">Infinite Scroll</span>
                            </label>
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="radio"
                                    name="productsLoading"
                                    value="Load More Button"
                                    checked={productsLoading === 'Load More Button'}
                                    onChange={() => onConfigChange('productsLoading', 'Load More Button')}
                                    className="tw-mr-2 tw-accent-[#091CAA]"
                                />
                                <span className="tw-text-sm tw-text-[#172B4D]">Load More Button</span>
                            </label>
                        </div>
                    </div>
                    
                    {/* Conditional fields based on Products Loading */}
                    {productsLoading === 'Infinite Scroll' ? (
                        <>
                            <div className="tw-mb-4">
                                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                    {`Show Scroll Bar after 'X' rows`}
                                </label>
                                <div className={styles.selectContainer}>
                                    <select
                                        value={scrollBarAfterRows}
                                        onChange={(e) => onConfigChange('scrollBarAfterRows', e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>
                                    </select>
                                    <div className={styles.selectIcon}>
                                        <DropdownIcon />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="tw-mb-4">
                                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                    {`Show 'Load More' button after 'X' products`}
                                </label>
                                <div className={styles.selectContainer}>
                                    <select
                                        value={showButtonAfterProducts}
                                        onChange={(e) => onConfigChange('showButtonAfterProducts', e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="20">20</option>
                                        <option value="25">25</option>
                                        <option value="30">30</option>
                                    </select>
                                    <div className={styles.selectIcon}>
                                        <DropdownIcon />
                                    </div>
                                </div>
                            </div>

                            <div className="tw-mb-4">
                                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                    {`Show Scroll Bar} after 'X' products`}
                                </label>
                                <div className={styles.selectContainer}>
                                    <select
                                        value={scrollBarAfterProducts}
                                        onChange={(e) => onConfigChange('scrollBarAfterProducts', e.target.value)}
                                        className={styles.select}
                                    >
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="20">20</option>
                                        <option value="25">25</option>
                                        <option value="30">30</option>
                                    </select>
                                    <div className={styles.selectIcon}>
                                        <DropdownIcon />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};